import { Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import React, { useCallback, useContext, useRef, useState } from 'react'
import * as yup from 'yup'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'

import api from 'api'
import { passwordHelperText } from 'constants/index'
import { AuthStateContext } from 'context/auth'
import FormikTextField from 'components/Common/FormikTextField'
import PromiseHandler from 'components/Common/PromiseHandler'
import ZendeskLink from 'components/Common/ZendeskLink'
import Button from 'components/Common/Button'
import { DISABLED_BUTTON, PRIMARY_BUTTON } from 'themes/theme'

export const emailFieldSchema = yup.string().email('Invalid Email Address').required('Email is required')

export const passwordFieldSchema = yup
  .string()
  .min(8, 'Password must be at least 8 characters')
  .required('Password is required')
  .matches(/[a-z]+/, 'Password requires lowercase letters')
  .matches(/[A-Z]+/, 'Password requires uppercase letters')
  .matches(/[0-9]+/, 'Password requires numbers')
  // https://stackoverflow.com/questions/18812317/javascript-regex-for-special-characters/18812336
  .matches(/[!@#$%^&*)(+=.<>{}/[\\\]:;'"|~`_-]+/, 'Password requires special characters')

export const passwordConfirmFieldSchema = yup
  .string()
  .required('Password confirm is required')
  .test('passwords-match', 'Passwords must match', (value, context) => {
    return context.parent.password === value
  })

export const firstName = yup
  .string()
  .required('First name is required')
  .matches(/.*\S.*/, 'First name is required')

export const lastName = yup
  .string()
  .required('Last name is required')
  .matches(/.*\S.*/, 'Last name is required')

const schema = yup.object({
  firstName: firstName,
  lastName: lastName,
  email: emailFieldSchema,
  password: passwordFieldSchema,
  passwordConfirm: passwordConfirmFieldSchema,
})

const sx = {
  root: {
    maxWidth: theme => theme.spacing(47),
  },
  textWrapper: {
    marginBottom: theme => theme.spacing(2),
  },
  button: {
    marginTop: theme => theme.spacing(3),
    marginBottom: theme => theme.spacing(2),
  },
  fieldWrapper: {
    minHeight: 85,
    marginBottom: theme => theme.spacing(1),
    width: '45%',
  },
  title: {
    lineHeight: '39px',
    color: 'var(--primary)',
  },
  checkboxLabel: {
    flex: 1,
    marginLeft: theme => theme.spacing(1),
  },
  chackboxFormControlRoot: {
    alignItems: 'flex-start',
    marginLeft: 0,
    marginRight: 0,
  },
  link: {
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
}

const PasswordText = styled(Box)(() => ({
  fontWeight: 'lighter',
  fontFamily: 'poppins',
}))

const initialValue = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirm: '',
}

export const RegisterAccountForm = ({ onNextStep }) => {
  const [authState, dispatch] = useContext(AuthStateContext)
  const formik = useRef(null)
  const [signUpPromise, setSignUpPromise] = useState(null)
  const currentInitialValues = authState.email
    ?
    { ...initialValue, email: authState.email.toLowerCase() }
    : initialValue

  const onFormSubmit = useCallback(
    (values, formikHelpers) => {
      let phoneNumber = `+1${values.mobileNumber}`
      const promise = values.mobileNumber
      ? api.signUp(values.email.toLowerCase(), values.password, values.firstName, values.lastName, phoneNumber)
      : api.signUp(values.email.toLowerCase(), values.password, values.firstName, values.lastName)

      formikHelpers.setSubmitting(true)
      dispatch({ type: 'signUpFormSubmitted', payload: values })
      setSignUpPromise(promise)
    },
    [dispatch],
  )

  const onSignUpSuccess = useCallback(() => {
    //TODO: Create user before confirmation
    //      BE team should set the USER as unverified
    onNextStep()
  }, [onNextStep])

  const onSignUpError = useCallback(error => {
    formik.current?.setSubmitting(false)

    if (error.message.includes('phone')) {
      formik.current?.setErrors({ mobileNumber: error.message })
    } else {
      formik.current?.setErrors({ email: error.message })
    }

  }, [])


  return (
    <>
      <PromiseHandler promise={signUpPromise} then={onSignUpSuccess} catch={onSignUpError} />
      <Typography
        variant="h1"
        style={{ textAlign: 'center', marginTop: '25px', fontSize: '24px' }}
        classes={{ root: sx.title }}
      >
        Let's create your account
      </Typography>
      <Formik
        innerRef={formik}
        validationSchema={schema}
        initialValues={currentInitialValues}
        onSubmit={onFormSubmit}
        validateOnBlur={true}
      >
        {({ isSubmitting, isValid }) => (
          <Form className="px-5" autoComplete="off">
            <Box display="flex" flexWrap="wrap" gap="20px">
              <Box sx={sx.fieldWrapper}>
                <FormikTextField required name="firstName" label="First Name" fullWidth />
              </Box>
              <Box sx={sx.fieldWrapper}>
                <FormikTextField required name="lastName" label="Last Name" fullWidth />
              </Box>
              <Box sx={sx.fieldWrapper}>
                <FormikTextField
                  required
                  name="email"
                  type="email"
                  label="Your Business Email"
                  fullWidth
                  autoComplete="off"
                />
              </Box>
              <Box sx={sx.fieldWrapper}>
                <FormikTextField
                  type="number"
                  name="mobileNumber"
                  label="Mobile Number"
                  fullWidth
                  className="hiddenArrow-input"
                />
              </Box>
              <Box sx={sx.fieldWrapper}>
                <FormikTextField
                  name="password"
                  label="Create A Password"
                  required
                  fullWidth
                  type="password"
                  helperText={<PasswordText>{passwordHelperText}</PasswordText>}
                  autoComplete="new-password"
                />
              </Box>
              <Box sx={sx.fieldWrapper}>
                <FormikTextField
                  name="passwordConfirm"
                  label="Confirm Your Password"
                  required
                  fullWidth
                  type="password"
                  autoComplete="off"
                />
              </Box>
            </Box>
            <Box textAlign="center">
              <Button
                style={{ width: '250px', margin: '20px' }}
                disabled={isSubmitting || !isValid}
                type={!isValid ? DISABLED_BUTTON : PRIMARY_BUTTON}
                submit
                loading={isSubmitting}
              >
                Next
              </Button>
            </Box>
            <Box marginTop="auto">
              <Box paddingX="60px" paddingY="10px" color="#66668F" textAlign="center" fontSize="12px" lineHeight="18px">
                By creating an account, I acknowledge I have read, understand, and agree to the Markaaz’s&nbsp;
                <a
                  rel="noreferrer"
                  className="authLink"
                  href="https://markaaz.com/terms-and-conditions"
                  target="_blank"
                >
                  Terms and Conditions
                </a>
                , including the arbitration, limitation of liability, and class waiver provisions. I also confirm I have
                read and understand the Markaaz &nbsp;
                <a rel="noreferrer" className="authLink" href="https://markaaz.com/privacy/" target="_blank">
                  Privacy Policy
                </a>
                .
              </Box>
              <ZendeskLink />
            </Box>
          </Form>
        )}
      </Formik>
    </>
  )
}
